import React, { useMemo, useState } from 'react';
import {
  FileText,
  Share2,
  Download,
  Target,
  Sparkles,
  ClipboardList,
  Gauge,
  Users,
  X,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import AnalysisResults from './AnalysisResults';
import SlideAnalysis from './SlideAnalysis';
import DuarteSlideCard from './DuarteSlideCard';
import DesignQualityPanel from './DesignQualityPanel';
import ShareReportModal from './ShareReportModal';
import { toast } from 'sonner';
import OpenAIService from '../services/openaiService';

const getSlideTitle = (slide, index) => {
  const raw = slide?.text_content || '';
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const titleLine = lines.find((line) => line.toUpperCase().startsWith('SLIDE TITLE:'));
  if (titleLine) {
    return titleLine.replace(/SLIDE TITLE:/i, '').trim() || `Slide ${slide?.slide_number || index + 1}`;
  }
  return lines[0] || `Slide ${slide?.slide_number || index + 1}`;
};

const getSlideHighlights = (slide) => {
  const raw = slide?.text_content || '';
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !/^slide (title|notes):/i.test(line));
  if (!lines.length) {
    return [];
  }
  const bulletLines = lines.filter((line) => /^[-•]/.test(line));
  const highlights = (bulletLines.length ? bulletLines : lines.slice(0, 4)).slice(0, 5);
  return highlights.map((text) => text.replace(/^[-•]\s*/, ''));
};

const DetailRow = ({ label, value }) => (
  <div className="grid gap-1">
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground leading-relaxed">{value || 'Not captured'}</p>
  </div>
);

const MetricCard = ({ icon: Icon, label, primary, secondary }) => (
  <div className="rounded-xl border border-border/50 bg-card/60 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold text-foreground">{primary || '—'}</p>
        {secondary && <p className="text-xs text-muted-foreground mt-1">{secondary}</p>}
      </div>
    </div>
  </div>
);

const SlideDetailCard = ({ slide, index }) => {
  const title = getSlideTitle(slide, index);
  const highlights = getSlideHighlights(slide);
  const colors = (slide?.colors || []).slice(0, 4);
  const fonts = (slide?.fonts || []).slice(0, 2);
  return (
    <article className="rounded-2xl border border-border/60 bg-card/70 shadow-sm overflow-hidden">
      <div className="grid gap-6 md:grid-cols-[minmax(220px,260px),1fr]">
        <div className="relative bg-muted/40">
          {slide?.thumbnail_url ? (
            <img src={slide.thumbnail_url} alt={`Slide ${slide?.slide_number || index + 1}`} className="h-full w-full object-contain bg-white" />
          ) : (
            <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 bg-muted/30 text-muted-foreground">
              <ClipboardList className="h-7 w-7" />
              <span className="text-xs">Slide {slide?.slide_number || index + 1}</span>
            </div>
          )}
          <span className="absolute left-4 top-4 rounded-full bg-background/90 px-2 py-1 text-xs font-medium text-foreground shadow">
            Slide {slide?.slide_number || index + 1}
          </span>
        </div>
        <div className="space-y-4 p-6">
          <header className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Slide title</p>
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            {slide?.notes && (
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>Speaker notes:</strong> {slide.notes.slice(0, 200)}{slide.notes.length > 200 ? '…' : ''}
              </p>
            )}
          </header>
          {highlights.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Key points</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {highlights.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
              <ClipboardList className="h-3.5 w-3.5" />
              {slide?.shapes_count ?? 0} elements
            </span>
            <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
              <Sparkles className="h-3.5 w-3.5" />
              {slide?.images ?? 0} images
            </span>
            <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
              <Gauge className="h-3.5 w-3.5" />
              {slide?.charts ?? 0} charts
            </span>
            {fonts.length > 0 && (
              <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
                <Users className="h-3.5 w-3.5" />
                {fonts.join(', ')}
              </span>
            )}
          </div>
          {colors.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Palette:</span>
              {colors.map((hex, idx) => (
                <span key={idx} className="flex items-center gap-2 rounded-full border border-border/40 bg-background/80 px-2 py-1 text-xs">
                  <span className="h-4 w-4 rounded-full border" style={{ backgroundColor: hex }} />
                  {hex}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const SlideReport = ({ slides, onGenerateVisual, onRewriteContent, onApplyAll, generatedContent, projectId, fileHash, isLargeFile }) => (
  <section className="space-y-8">
    <div className="space-y-2">
      <h2 className="text-2xl font-semibold text-foreground">Detailed guidance</h2>
      <p className="text-sm text-muted-foreground max-w-3xl">
        Design recommendations for each slide based on content analysis and visual best practices
      </p>
      {isLargeFile && (
        <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Large file detected</p>
              <p className="text-yellow-700 mt-1">
                Slide previews load on-demand to prevent memory issues. Click "Generate Preview" on each slide to view its thumbnail.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
    <div className="space-y-8">
      {slides.length ? (
        slides.map((slide, index) => (
          <DuarteSlideCard
            key={index}
            slide={slide}
            index={index}
            onGenerateVisual={onGenerateVisual}
            onRewriteContent={onRewriteContent}
            onApplyAll={onApplyAll}
            generatedContent={generatedContent[slide.slide_number]}
            projectId={projectId}
            fileHash={fileHash}
            isLargeFile={isLargeFile}
          />
        ))
      ) : (
        <div className="rounded-xl border border-dashed border-border/60 bg-card/60 p-10 text-center text-sm text-muted-foreground">
          Slide metadata was not returned with this analysis.
        </div>
      )}
    </div>
  </section>
);

const CombinedAnalysisWorkspace = ({
  analysis,
  slides = [],
  uploadedFile,
  uploadedFileName,
  onExportReport,
  onShare,
  onDownloadBlueprint,
  onExportJSON,
  onExportQuestions,
  onExportDesignBrief,
  children,
  apiKey,
  projectId, // NEW: Project ID for thumbnails
}) => {
  const [activeView, setActiveView] = useState('overview');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ type: null, data: null, loading: false });
  const [generatedContent, setGeneratedContent] = useState({}); // Persist generated content by slide number
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const projectName = uploadedFile?.name || uploadedFileName || analysis?.uploadedFileName || 'Untitled deck';

  const handleShare = () => {
    setShareModalOpen(true);
  };

  // Extract metadata for thumbnail generation
  const fileHash = analysis?.file_hash || analysis?.metadata?.file_hash || slides[0]?.file_hash;
  const isLargeFile = analysis?.metadata?.is_large_file || false;
  const priorityFixes = analysis?.executionGuidance?.priorityFixes || [];
  const quickWins = analysis?.executionGuidance?.quickWins || [];

  const handleGenerateVisual = async (slide, visualContent) => {
    if (!apiKey) {
      toast.error('API key not configured');
      return;
    }

    // Create a detailed, contextual prompt
    const slideTitle = slide.duarte_title || slide.title || `Slide ${slide.slide_number}`;
    const slideContent = slide.text_content || '';
    const presentationType = analysis?.presentationType?.primary || 'presentation';

    // Build a rich prompt with context
    const contextualPrompt = `Create a professional, high-quality visual for a ${presentationType} slide titled "${slideTitle}".

Slide context: ${slideContent.substring(0, 200)}

Design requirements: ${visualContent.join('. ')}

Style: Modern, professional, clean design suitable for business presentations. Use relevant imagery, appropriate colors, and ensure visual clarity.`;

    const prompt = contextualPrompt;

    setModalContent({
      type: 'visual',
      data: {
        slideNumber: slide.slide_number,
        prompt,
        slide,
      },
      loading: true,
    });
    setModalOpen(true);

    try {
      const openaiService = new OpenAIService(apiKey);
      const result = await openaiService.generateImage(prompt, { size: '1024x1024' });

      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          imageUrl: result.imageUrl,
          revisedPrompt: result.revisedPrompt,
        },
      }));

      // Persist generated image
      setGeneratedContent(prev => ({
        ...prev,
        [slide.slide_number]: {
          ...prev[slide.slide_number],
          imageUrl: result.imageUrl,
          revisedPrompt: result.revisedPrompt,
        },
      }));

      toast.success('Visual generated successfully!');
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error(`Failed to generate image: ${error.message}`);
      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          error: error.message,
        },
      }));
    }
  };

  const handleRewriteContent = async (slide, contentRefinements, newTitle) => {
    if (!apiKey) {
      toast.error('API key not configured');
      return;
    }

    setModalContent({
      type: 'content',
      data: {
        slideNumber: slide.slide_number,
        newTitle,
        refinements: contentRefinements,
        slide,
      },
      loading: true,
    });
    setModalOpen(true);

    try {
      const openaiService = new OpenAIService(apiKey);

      const prompt = `Rewrite the following slide content based on these recommendations:

Slide Title: ${slide.text_content?.split('\n')[0] || 'Untitled'}
Current Content: ${slide.text_content || ''}

New Title: ${newTitle}

Refinement Recommendations:
${contentRefinements.map((r, i) => `${i + 1}. ${r}`).join('\n')}

Please provide:
1. A rewritten slide title
2. Rewritten slide body content that incorporates all refinement recommendations
3. Keep it concise and presentation-ready

Format your response as JSON:
{
  "title": "...",
  "body": "..."
}`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            { role: 'system', content: 'You are a presentation design expert. Provide concise, impactful slide content.' },
            { role: 'user', content: prompt }
          ],
          temperature: 1,
          max_completion_tokens: 64000,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamingContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content || '';
              streamingContent += content;

              // Update modal with streaming content
              setModalContent(prev => ({
                ...prev,
                loading: false,
                data: {
                  ...prev.data,
                  streamingText: streamingContent,
                },
              }));
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      // Parse final JSON content
      const rewrittenContent = JSON.parse(streamingContent);

      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          rewritten: rewrittenContent,
          streamingText: null,
        },
      }));

      // Persist rewritten content
      setGeneratedContent(prev => ({
        ...prev,
        [slide.slide_number]: {
          ...prev[slide.slide_number],
          rewritten: rewrittenContent,
        },
      }));

      toast.success('Content rewritten successfully!');
    } catch (error) {
      console.error('Content rewrite error:', error);
      toast.error(`Failed to rewrite content: ${error.message}`);
      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          error: error.message,
        },
      }));
    }
  };

  const handleApplyAll = async (slide, contentRefinements, visualContent, newTitle) => {
    if (!apiKey) {
      toast.error('API key not configured');
      return;
    }

    setModalContent({
      type: 'apply-all',
      data: {
        slideNumber: slide.slide_number,
        slide,
        contentRefinements,
        visualContent,
        newTitle,
      },
      loading: true,
    });
    setModalOpen(true);

    try {
      // Run both operations in parallel
      const promises = [];

      if (visualContent.length > 0) {
        const slideTitle = slide.duarte_title || slide.title || `Slide ${slide.slide_number}`;
        const slideContent = slide.text_content || '';
        const presentationType = analysis?.presentationType?.primary || 'presentation';

        const contextualPrompt = `Create a professional, high-quality visual for a ${presentationType} slide titled "${slideTitle}".

Slide context: ${slideContent.substring(0, 200)}

Design requirements: ${visualContent.join('. ')}

Style: Modern, professional, clean design suitable for business presentations. Use relevant imagery, appropriate colors, and ensure visual clarity.`;

        const openaiService = new OpenAIService(apiKey);
        promises.push(openaiService.generateImage(contextualPrompt, { size: '1024x1024' }));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (contentRefinements.length > 0) {
        const prompt = `You are a presentation design expert. Rewrite the content for this slide following these guidelines:

Slide Title: ${newTitle}
Current Content: ${slide.text_content}

Refinement Guidelines:
${contentRefinements.map((r, i) => `${i + 1}. ${r}`).join('\n')}

Provide a JSON response with:
{
  "title": "improved slide title",
  "bullets": ["bullet 1", "bullet 2", ...],
  "notes": "speaker notes or additional context"
}`;

        promises.push(
          fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
              model: 'gpt-5-mini',
              messages: [
                { role: 'system', content: 'You are a presentation design expert. Provide concise, impactful slide content.' },
                { role: 'user', content: prompt }
              ],
              temperature: 1,
              max_completion_tokens: 64000,
            }),
          }).then(res => res.json())
        );
      } else {
        promises.push(Promise.resolve(null));
      }

      const [imageResult, contentResult] = await Promise.all(promises);

      const results = {};
      if (imageResult) {
        results.image = {
          imageUrl: imageResult.imageUrl,
          revisedPrompt: imageResult.revisedPrompt,
        };
      }
      if (contentResult) {
        results.content = JSON.parse(contentResult.choices[0].message.content);
      }

      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          results,
        },
      }));

      // Persist generated content
      setGeneratedContent(prev => ({
        ...prev,
        [slide.slide_number]: {
          ...prev[slide.slide_number],
          ...(results.image && { imageUrl: results.image.imageUrl, revisedPrompt: results.image.revisedPrompt }),
          ...(results.content && { rewritten: results.content }),
        },
      }));

      toast.success('All changes applied successfully!');
    } catch (error) {
      console.error('Apply all error:', error);
      toast.error(`Failed to apply changes: ${error.message}`);
      setModalContent(prev => ({
        ...prev,
        loading: false,
        data: {
          ...prev.data,
          error: error.message,
        },
      }));
    }
  };

  const overview = useMemo(
    () => [
      {
        label: 'Big idea',
        value:
          analysis?.strategicDirection?.primaryStrategy ||
          analysis?.contextualGrounding?.identifiedObjective ||
          'Not captured in analysis.',
      },
      {
        label: 'Audience transformation',
        value:
          analysis?.contextualGrounding?.audienceProfile ||
          analysis?.storytellingStructure?.narrativeApproach ||
          'Audience details not provided.',
      },
      {
        label: 'Move from',
        value:
          analysis?.storytellingStructure?.flowRecommendations?.[0] ||
          priorityFixes[0] ||
          'No transition guidance available.',
      },
      {
        label: 'Move to',
        value:
          analysis?.storytellingStructure?.flowRecommendations?.[1] ||
          quickWins[0] ||
          'No destination guidance provided.',
      },
    ],
    [analysis, priorityFixes, quickWins]
  );

  const metrics = useMemo(
    () => [
      {
        icon: ClipboardList,
        label: 'Presentation type',
        primary: analysis?.presentationType?.primary || 'Unclassified',
        secondary: analysis?.presentationType?.secondary || undefined,
      },
      {
        icon: Gauge,
        label: 'Overall score',
        primary:
          analysis?.presentationScore?.overall !== undefined
            ? `${Math.round(analysis.presentationScore.overall)} / 100`
            : 'Not scored',
        secondary:
          analysis?.presentationScore?.trend ? `Trend: ${analysis.presentationScore.trend}` : undefined,
      },
      {
        icon: Users,
        label: 'Audience focus',
        primary: analysis?.contextualGrounding?.audienceProfile || 'Not specified',
        secondary:
          analysis?.contextualGrounding?.engagementLevel ? `Engagement: ${analysis.contextualGrounding.engagementLevel}` : undefined,
      },
    ],
    [analysis]
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className={`mx-auto px-6 py-10 ${activeView === 'slides' ? 'max-w-[1800px]' : 'max-w-[1400px] grid gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(260px,1fr)]'}`}>
        <main className={`space-y-12 ${activeView === 'slides' ? 'mx-auto w-full' : ''}`}>
          <header className="space-y-6 rounded-3xl border border-border/40 bg-card/80 p-8 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>Analysis report</span>
                </div>
                <h1 className="text-3xl font-semibold text-foreground">Content improvement report</h1>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Comprehensive review of <strong>{projectName}</strong> generated with the Design Compass methodology. Use this report to brief your team and prioritise the next edit round.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={onShare || handleShare}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:border-primary/50 hover:text-primary"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
                {onDownloadBlueprint && (
                  <button
                    type="button"
                    onClick={onDownloadBlueprint}
                    className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20"
                  >
                    <Target className="h-4 w-4" />
                    Design blueprint
                  </button>
                )}
                {onExportReport && (
                  <button
                    type="button"
                    onClick={onExportReport}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90"
                  >
                    <Download className="h-4 w-4" />
                    Export PDF
                  </button>
                )}
              </div>
            </div>

          </header>

          {false ? (
            <>
              <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {metrics.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </section>
              <AnalysisResults
                analysis={analysis}
                onExportPDF={onExportReport}
                onExportJSON={onExportJSON}
                onExportQuestions={onExportQuestions}
                onExportDesignBrief={onExportDesignBrief}
                onDownloadBlueprint={onDownloadBlueprint}
                uploadedFileName={projectName}
                apiKey={apiKey}
              />
              {(priorityFixes.length > 0 || quickWins.length > 0) && (
                <section className="grid gap-6 md:grid-cols-2">
                  <div className="rounded-2xl border border-border/50 bg-card/70 p-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-foreground">Priority fixes</h2>
                    <p className="text-xs text-muted-foreground mb-4">Tackle these before your next client review.</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {(priorityFixes.length ? priorityFixes : ['No priority fixes identified.']).map((fix, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-destructive" />
                          <span>{fix}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-border/50 bg-card/70 p-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-foreground">Quick wins</h2>
                    <p className="text-xs text-muted-foreground mb-4">Low-effort improvements you can ship today.</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {(quickWins.length ? quickWins : ['No quick wins identified.']).map((win, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                          <span>{win}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}
            </>
          ) : (
            <SlideReport
              slides={slides}
              onGenerateVisual={handleGenerateVisual}
              onRewriteContent={handleRewriteContent}
              onApplyAll={handleApplyAll}
              generatedContent={generatedContent}
              projectId={projectId}
              fileHash={fileHash}
              isLargeFile={isLargeFile}
            />
          )}
        </main>

        {activeView !== 'slides' && <aside className="space-y-6">{children}</aside>}
      </div>

      {/* Modal for displaying generated content */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modalContent.type === 'visual' ? 'Generate Visual' :
               modalContent.type === 'apply-all' ? 'Apply All Changes' :
               'Rewrite Content'}
            </DialogTitle>
            <DialogDescription>
              Slide {modalContent.data?.slideNumber}
            </DialogDescription>
          </DialogHeader>

          {modalContent.type === 'visual' && (
            <div className="space-y-4">
              {modalContent.loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">Generating visual...</p>
                </div>
              ) : modalContent.data?.error ? (
                <div className="text-center py-4 text-destructive">
                  <p className="font-semibold">Error generating image</p>
                  <p className="text-sm mt-2">{modalContent.data.error}</p>
                </div>
              ) : modalContent.data?.imageUrl ? (
                <>
                  <div>
                    <h4 className="font-semibold mb-2">Generated Visual:</h4>
                    <div className="flex items-center justify-center bg-muted/20 rounded-md p-4">
                      <img
                        src={modalContent.data.imageUrl}
                        alt="Generated visual"
                        className="max-w-full max-h-[500px] object-contain rounded-md border border-border"
                      />
                    </div>
                  </div>
                  {modalContent.data.revisedPrompt && (
                    <div>
                      <h4 className="font-semibold mb-2">Prompt Used:</h4>
                      <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                        {modalContent.data.revisedPrompt}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = modalContent.data.imageUrl;
                        link.download = `slide-${modalContent.data.slideNumber}-visual.png`;
                        link.click();
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Image
                    </Button>
                  </div>
                </>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Visual Prompt:</h4>
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                    {modalContent.data?.prompt}
                  </p>
                </div>
              )}
            </div>
          )}

          {modalContent.type === 'content' && (
            <div className="space-y-4">
              {modalContent.loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">Rewriting content...</p>
                </div>
              ) : modalContent.data?.streamingText ? (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Streaming Content...
                  </h4>
                  <div className="text-sm bg-muted p-4 rounded-md whitespace-pre-wrap font-mono">
                    {modalContent.data.streamingText}
                  </div>
                </div>
              ) : modalContent.data?.error ? (
                <div className="text-center py-4 text-destructive">
                  <p className="font-semibold">Error rewriting content</p>
                  <p className="text-sm mt-2">{modalContent.data.error}</p>
                </div>
              ) : modalContent.data?.rewritten ? (
                <>
                  <div>
                    <h4 className="font-semibold mb-2">Rewritten Title:</h4>
                    <p className="text-lg font-semibold bg-muted p-3 rounded-md">
                      {modalContent.data.rewritten.title}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Rewritten Content:</h4>
                    <div className="text-sm bg-muted p-4 rounded-md whitespace-pre-wrap">
                      {modalContent.data.rewritten.body}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const content = `${modalContent.data.rewritten.title}\n\n${modalContent.data.rewritten.body}`;
                        navigator.clipboard.writeText(content);
                        toast.success('Copied to clipboard!');
                      }}
                    >
                      Copy to Clipboard
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h4 className="font-semibold mb-2">New Title:</h4>
                    <p className="text-sm bg-muted p-3 rounded-md">
                      {modalContent.data?.newTitle}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Content Refinements:</h4>
                    <ul className="space-y-2">
                      {modalContent.data?.refinements?.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {modalContent.type === 'apply-all' && (
            <div className="space-y-6">
              {modalContent.loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">Applying all changes...</p>
                </div>
              ) : modalContent.data?.error ? (
                <div className="text-center py-4 text-destructive">
                  <p className="font-semibold">Error applying changes</p>
                  <p className="text-sm mt-2">{modalContent.data.error}</p>
                </div>
              ) : modalContent.data?.results ? (
                <>
                  {modalContent.data.results.image && (
                    <div>
                      <h4 className="font-semibold mb-2">Generated Visual:</h4>
                      <img
                        src={modalContent.data.results.image.imageUrl}
                        alt="Generated visual"
                        className="w-full rounded-md border border-border"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = modalContent.data.results.image.imageUrl;
                          link.download = `slide-${modalContent.data.slideNumber}-visual.png`;
                          link.click();
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Image
                      </Button>
                    </div>
                  )}

                  {modalContent.data.results.content && (
                    <div>
                      <h4 className="font-semibold mb-2">Rewritten Content:</h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Title:</p>
                          <p className="font-semibold bg-muted p-2 rounded-md">
                            {modalContent.data.results.content.title}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Content:</p>
                          <ul className="space-y-1 bg-muted p-3 rounded-md">
                            {modalContent.data.results.content.bullets?.map((bullet, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                                <span>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        {modalContent.data.results.content.notes && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Speaker Notes:</p>
                            <p className="text-sm bg-muted p-2 rounded-md">
                              {modalContent.data.results.content.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Report Modal */}
      <ShareReportModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        analysisData={analysis}
        presentationName={projectName}
      />
    </div>
  );
};

export default CombinedAnalysisWorkspace;
